import axiosInstance from 'plugins/axios';
import { IBodyResponse, IPagination } from 'types/type.global';
import { IGroup } from 'types/user';
import { IGeoCoords } from 'types/previewCampaign';
import {
    IListCampaignsResponse,
    ICampaignResponse,
    IListCampaignsPayload,
    ICampaignListQuestionResponse,
    ICampaignQuestionDetailResponse,
    IQuestionLogicResponse,
    IQuestionAnswerOptionsResponse,
    IQuestionLogicDisplayAnswerResponse,
    IQuestionLogicSingleResponse,
    ICampaignOverviewResponse,
    ICamapaignDataAnalysisResponse,
    ICampaignAnalyticsQuestionDetail,
    ICampaignDataStatisticsSummaryResponse,
    ListQuestionDataFilter,
    IListQuestionFilterResponse,
    ICampaignDataQuestion,
} from 'types/campaign';
import {
    IQuestionNewEditForm,
    ISkipQuestionLogic,
    IAnswerFromOtherQuestionLogic,
    // IBranchQuestionLogic,
} from 'types/campaign-design';
import { objectToFormData, applyTokenToUrl } from 'utils/helpers';

export const allCampaigns = async (): Promise<
    IBodyResponse<IListCampaignsResponse>
> => {
    const response = await axiosInstance.get('admin/surveys');
    if (!response.data.success) {
        throw new Error(response.data.message || '');
    }
    return response.data;
};

export const listCampaigns = async (
    params?: IListCampaignsPayload
): Promise<IBodyResponse<IListCampaignsResponse>> => {
    const response = await axiosInstance.get('admin/surveys', {
        params,
    });
    if (!response.data.success) {
        throw new Error(response.data.message || '');
    }
    return response.data;
};

export const copyCampaign = async (
    id: number
): Promise<IBodyResponse<ICampaignResponse>> => {
    const response = await axiosInstance.post(`admin/surveys/${id}/copy`);
    if (!response.data.success) {
        throw new Error(response.data.message || '');
    }
    return response.data;
};

export const deleteCampaign = async (
    id: number
): Promise<IBodyResponse<null>> => {
    const response = await axiosInstance.delete(`admin/surveys/${id}`);
    if (!response.data.success) {
        throw new Error(response.data.message || '');
    }
    return response.data;
};

export const createCampaign = async (
    formData: FormData
): Promise<IBodyResponse<null>> => {
    const response = await axiosInstance.post('admin/surveys', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const editCampaign = async (
    formData: FormData,
    id: number
): Promise<IBodyResponse<ICampaignResponse>> => {
    const response = await axiosInstance.put(`admin/surveys/${id}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const importQuestionExcel = async (
    formData: FormData
): Promise<IBodyResponse<null>> => {
    const response = await axiosInstance.post(
        'admin/surveys/import_excel',
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );
    return response.data;
};

export const downloadFileError = async (): Promise<
    IBodyResponse<{ file_url: string }>
> => {
    const response = await axiosInstance.get(
        'admin/surveys/download_import_excel_error'
    );
    return response.data;
};

export const campaignDetail = async (
    campaignId: number | string
): Promise<IBodyResponse<ICampaignResponse>> => {
    const response = await axiosInstance.get(`admin/surveys/${campaignId}`);
    if (!response.data.success) {
        throw new Error(response.data.message || '');
    }
    return response.data;
};

export const campaignOverview = async (
    campaignId: number,
    params: any // eslint-disable-line
): Promise<IBodyResponse<ICampaignOverviewResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/overview`,
        {
            params,
        }
    );
    if (!response.data.success) {
        throw new Error(response.data.message || '');
    }
    return response.data;
};

export const listCampaignQuestions = async (
    campaignId: number
): Promise<IBodyResponse<ICampaignListQuestionResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions`
    );
    return response.data;
};

export const campaignQuestionDetail = async (
    campaignId: string | number | null,
    questionId: string | number | null
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions/${questionId}`
    );
    return response.data;
};

export const updateCampaignQuestion = async (
    campaignId: number,
    data: IQuestionNewEditForm
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const formData = objectToFormData(data);
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/questions/${data.id}`,
        formData
    );
    return response.data;
};

export const createCampaignQuestion = async (
    campaignId: number,
    data: IQuestionNewEditForm
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const formData = objectToFormData(data);
    const response = await axiosInstance.post(
        `admin/surveys/${campaignId}/questions`,
        formData
    );
    return response.data;
};

export const swapCampaignQuestion = async (
    campaignId: number,
    data: { from: number; to: number }
): Promise<
    IBodyResponse<{
        from: any; // eslint-disable-line
    }>
> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/questions/swap`,
        data
    );
    return response.data;
};

export const deleteCampaignQuestion = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<null>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}`
    );
    return response.data;
};

export const markCampaignQuestionAsRequired = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<null>> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/questions/${questionId}/mark_as_require`
    );
    return response.data;
};

export const skipLogicOptions = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<IQuestionLogicResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions/${questionId}/skip_logic_options`
    );
    return response.data;
};

export const questionAnswerOptions = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<IQuestionAnswerOptionsResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions/${questionId}/question_answer_options_as_options`
    );
    return response.data;
};

export const saveSkipLogicOptions = async (
    campaignId: number,
    questionId: number,
    data: ISkipQuestionLogic
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/questions/${questionId}/save_skip_logic_options`,
        data
    );
    return response.data;
};

export const creeningLogicOptions = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<IQuestionLogicResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions/${questionId}/creening_logic_options`
    );
    return response.data;
};

export const saveCreeningLogicOptions = async (
    campaignId: number,
    questionId: number,
    data: ISkipQuestionLogic
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/questions/${questionId}/save_creening_logic_options`,
        data
    );
    return response.data;
};

export const duplicateCampaignQuestion = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.post(
        `admin/surveys/${campaignId}/questions/${questionId}/duplicate`
    );
    return response.data;
};

export const deleteQuestionSkipLogic = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}/delete_skip_logic`
    );
    return response.data;
};

export const deleteQuestionCreeningLogic = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}/delete_creening_logic`
    );
    return response.data;
};

export const questionTitleLogicOptions = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<IQuestionLogicDisplayAnswerResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions/${questionId}/question_title_logic_options`
    );
    return response.data;
};

export const deleteQuestionTitleLogic = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}/delete_question_title_logic`
    );
    return response.data;
};

export const answerFromOtherQuestionOptions = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<IQuestionLogicSingleResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions/${questionId}/answer_from_other_question_options`
    );
    return response.data;
};

export const saveAnswerFromOtherQuestionOptions = async (
    campaignId: number,
    questionId: number,
    data: IAnswerFromOtherQuestionLogic
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/questions/${questionId}/save_answer_from_other_question_options`,
        data
    );
    return response.data;
};

export const deleteAnswerFromOtherQuestionLogic = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}/delete_answer_from_other_question_logic`
    );
    return response.data;
};

export const branchLogicOptions = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<IQuestionLogicResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions/${questionId}/branch_logic_options`
    );
    return response.data;
};

export const saveBranchLogicOptions = async (
    campaignId: number,
    questionId: number,
    data: any // eslint-disable-line
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/questions/${questionId}/save_branch_logic_options`,
        data
    );
    return response.data;
};

export const deleteQuestionBranchLogic = async (
    campaignId: number,
    questionId: number
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}/delete_branch_logic`
    );
    return response.data;
};

export const activeSurveyFree = async (
    campaignId: number
): Promise<IBodyResponse<ICampaignResponse>> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/active_survey_free`
    );
    return response.data;
};

export const updateStatusCampaign = async (
    campaignId: number,
    status: string
): Promise<IBodyResponse<ICampaignResponse>> => {
    const response = await axiosInstance.put(
        `admin/surveys/${campaignId}/update_status`,
        { status_client: status }
    );
    return response.data;
};

export const joinSurvey = async (
    surveyCode: string,
    code?: string,
    staff_id?: string
): Promise<
    IBodyResponse<{
        token: string;
        code?: string;
        staff_id?: string;
        error_code?: string;
        groups?: IGroup[];
    }>
> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
        survey_code: surveyCode,
    };
    if (code) {
        payload.code = code;
    }
    if (staff_id) {
        payload.staff_id = staff_id;
    }
    const response = await axiosInstance.post(`publics/join_survey`, payload);
    return response.data;
};

// Lấy theme (group) theo survey id — public, không cần mã/đăng nhập.
// Dùng cho màn nhập mã bảo mật để apply theme trước khi join.
export const fetchSurveyGroup = async (
    campaignId: number | string
): Promise<IBodyResponse<{ groups: IGroup[] }>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/group`
    );
    return response.data;
};

export const getCampaignAnalyticsFeedback = async (
    id: number
): Promise<IBodyResponse<ICamapaignDataAnalysisResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${id}/show_survey_analysis`
    );
    return response.data;
};

export const getCampaignAnalyticsQuestionFeedback = async (
    id: number,
    pagination: IPagination
): Promise<IBodyResponse<ICampaignDataQuestion>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${id}/show_question_analysis`,
        {
            params: pagination,
        }
    );
    return response.data;
};

export const getCampaignAnalyticsQuestionDetail = async (
    campaignId: number,
    questionId: number,
    pagination: IPagination
): Promise<IBodyResponse<ICampaignAnalyticsQuestionDetail>> => {
    const response = await axiosInstance.get(
        `/admin/surveys/${campaignId}/questions/${questionId}/show_answer_detail`,
        {
            params: pagination,
        }
    );
    return response?.data;
};

export const getCampaignAnalyticsQuestionTextFilter = async (
    params: any, // eslint-disable-line
    pagination: IPagination
): Promise<IBodyResponse<ICampaignAnalyticsQuestionDetail>> => {
    const response = await axiosInstance.post(
        `/admin/statistics/filter_question_text?page=${pagination.page}&per_page=${pagination.per_page}`,
        params
    );
    return response?.data;
};

export const getCampaignDataStatisticsSummary = async (
    campaignId: number
): Promise<IBodyResponse<ICampaignDataStatisticsSummaryResponse>> => {
    const response = await axiosInstance.get(
        `admin/statistics/summary?survey_id=${campaignId}`
    );
    return response.data;
};

export const getListQuestionDataFilter = async (
    campaignId: number,
    questionIds: string[],
    decimal_setting: string
): Promise<IBodyResponse<ListQuestionDataFilter>> => {
    const response = await axiosInstance.get(
        `admin/statistics?survey_id=${campaignId}&decimal_setting=${decimal_setting}&question_ids=${questionIds?.join(',')}`
    );
    return response.data;
};

export const getListQuestionFilter = async (
    campaignId: number,
    type: string
): Promise<IBodyResponse<IListQuestionFilterResponse[]>> => {
    const response = await axiosInstance.get(
        `admin/statistics/question_filters?survey_id=${campaignId}&type=${type}`
    );
    return response.data;
};

export const filterQuestionData = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
): Promise<IBodyResponse<ListQuestionDataFilter>> => {
    const response = await axiosInstance.post(`/admin/statistics/filter_data`, {
        filter: data,
    });
    return response.data;
};

export const compareQuestionData = async (
    params: {
        survey_id: number;
        question_id: string;
        answer_option_ids: any; // eslint-disable-line
    }[]
): Promise<IBodyResponse<ListQuestionDataFilter>> => {
    const response = await axiosInstance.post(
        `/admin/statistics/compare`,
        params
    );
    return response.data;
};

export const exportDataExcelAnswerRaw = async (
    campaignId: number,
    data: any // eslint-disable-line
): Promise<IBodyResponse<{ url: string }>> => {
    const response = await axiosInstance.post(
        `admin/surveys/${campaignId}/export_data_excel_answer_raw`,
        data
    );
    return response.data;
};

export const exportCampaigns = async (
    params: any // eslint-disable-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
    const response = await axiosInstance.get('admin/surveys/export_data', {
        params,
        responseType: 'blob',
    });

    return response.data;
};

export const deleteAnswerOptions = async (
    campaignId: number,
    questionId: number,
    answerOptionId: number
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}/delete_answer_options/${answerOptionId}`
    );
    return response.data;
};

export const deleteTableComponent = async (
    campaignId: number,
    questionId: number,
    tableComponentId: string
): Promise<IBodyResponse<ICampaignQuestionDetailResponse>> => {
    const response = await axiosInstance.delete(
        `admin/surveys/${campaignId}/questions/${questionId}/delete_table_component/${tableComponentId}`
    );
    return response.data;
};

export const listQuestionsForTabulation = async (
    campaignId: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/questions_for_tabulation`
    );
    return response.data;
};

export const exportTabulationData = async (
    campaignId: number,
    params: any[] // eslint-disable-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<{ url: string }>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${campaignId}/export_tabulation_data`,
        {
            params: {
                chose_options: params,
            },
        }
    );
    return response.data;
};
type IAnswersRow = Record<string, string | number | null>;

export const saveSurveyAnswersToThirdParty = async (
    surveyCode: string,
    data: {
        rows: IAnswersRow[];
    },
    geo?: IGeoCoords | null
): Promise<IBodyResponse<null>> => {
    const response = await axiosInstance.post(
        applyTokenToUrl(`preview_survey_ipad/${surveyCode}/answers`),
        {
            answers: data,
            // Đặt ngang cấp answers: rows map theo tên câu hỏi để đẩy sang
            // third-party, nhét toạ độ vào đó sẽ thành một câu hỏi giả.
            ...(geo && { lat: geo.lat, lng: geo.long }),
        }
    );
    return response.data;
};

export const getQuestionBranchLogicOptions = async (
    token: string | null,
    campaignId: number | string,
    questionId: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<{ question: any }>> => {
    const url = token
        ? `preview_survey/${campaignId}/infos/${questionId}?token=${token}`
        : `preview_survey/${campaignId}/infos/${questionId}`;
    const response = await axiosInstance.get(url);
    return response.data;
};
